import { Edge, useEdgesState, useNodesState } from '@xyflow/react';
import { usePedigreeContext } from '../context/usePedigreeContext';
import { useCallback, useEffect } from 'react';
import PositionedGraph from '../model/positionedGraph';
import DynamicPositionedGraph from '../model/dynamicGraph';
import {
  PedigreeNode,
  PedigreePersonNode,
  PedigreeRelationshipNode,
  PedigreePersonNodeData,
  PedigreeRelationshipNodeData,
  PedigreeGender,
} from '../types';
import { useGraphData } from './useGraphData';
import { isNil } from 'lodash';
import { detectOverlappingEdges } from './pedigree-hook-helpers';
import {
  DEFAULT_STEP_HEIGHT,
  EDGE_STYLE,
  PERSON_NODE_DIMENSIONS,
  RELATIONSHIP_NODE_DIMENSIONS,
} from '../visual-consts';

// -----------------------------------------------------------------------------
const genderMap: Record<string, PedigreeGender> = {
  M: 'M',
  F: 'F',
  U: 'U',
};

// -----------------------------------------------------------------------------
export const useReactflowState = () => {
  const {
    baseGraph,
    positionedGraph: _positionedGraph,
    dynamicGraph: _dynamicGraph,
    refreshFlag,
    setPositionedGraph,
    setDynamicGraph,
    setRefreshFlag,
    initializeGraphs,
  } = usePedigreeContext();
  const { getNodeData } = useGraphData();
  const [nodes, setNodes, onNodesChange] = useNodesState([] as PedigreeNode[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!initializeGraphs) {
      return;
    }

    if (!baseGraph) {
      initializeGraphs();
      return;
    }

    if (baseGraph.getNumVertices() === 0) {
      initializeGraphs();
    }
  }, [initializeGraphs, baseGraph]);

  // ---------------------------------------------------------------------------
  const doTheGraphThing = useCallback(() => {
    if (!baseGraph) {
      return;
    }

    const { nodes: flowNodes, edges: flowEdges } =
      convertPedigreeToFlow(baseGraph);

    setNodes(flowNodes);
    setEdges(flowEdges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseGraph, setEdges, setNodes]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    doTheGraphThing();
  }, [doTheGraphThing]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!refreshFlag) {
      return;
    }

    doTheGraphThing();
    setRefreshFlag(false);
  }, [refreshFlag, setRefreshFlag, doTheGraphThing]);

  // ---------------------------------------------------------------------------
  // Helper function to initialize positioning graphs
  const initializePedigreeGraphs = (graph: any) => {
    const pg = new PositionedGraph(graph, 150, 150);
    pg.yDistanceNodeToChildhub = 150;
    pg.yDistanceChildhubToNode = 150;
    const dg = new DynamicPositionedGraph(pg);
    return { pg, dg };
  };

  // ---------------------------------------------------------------------------
  // Helper function to count partners for each person
  const countPartners = (graph: any) => {
    const partnerRelationships = new Map<number, Set<number>>();

    for (let i = 0; i <= graph.getMaxRealVertexId(); i++) {
      if (graph.isRelationship(i)) {
        const parents = graph.getParents(i);
        if (parents.length === 2) {
          const [parent1, parent2] = parents;

          // Initialize sets if they don't exist
          if (!partnerRelationships.has(parent1)) {
            partnerRelationships.set(parent1, new Set());
          }
          if (!partnerRelationships.has(parent2)) {
            partnerRelationships.set(parent2, new Set());
          }

          // Add the relationship to both parents' sets
          partnerRelationships.get(parent1)?.add(i);
          partnerRelationships.get(parent2)?.add(i);
        }
      }
    }

    return partnerRelationships;
  };

  // ---------------------------------------------------------------------------
  // Helper function to create person nodes
  const createPersonNode = (
    graph: any,
    dg: any,
    vertexId: number
  ): PedigreePersonNode => {
    const pos = dg.getPosition(vertexId);
    const props = graph.properties[vertexId];
    const name = graph.getVertexNameById(vertexId);
    const gender = genderMap[props?.gender] || 'unknown';
    return {
      id: vertexId.toString(),
      type: 'person',
      position: {
        x: pos.x || 0,
        y: pos.y || 0,
      },
      data: {
        id: vertexId.toString(),
        label: name || `Person ${vertexId}`,
        gender: gender,
        isProband: props?.isProband || false,
        originalId: vertexId,
      } as PedigreePersonNodeData,
    };
  };

  // ---------------------------------------------------------------------------
  // Helper function to create relationship nodes
  const createRelationshipNode = (
    dg: any,
    vertexId: number
  ): PedigreeRelationshipNode => {
    const pos = dg.getPosition(vertexId);
    return {
      id: vertexId.toString(),
      type: 'relationship',
      position: {
        x:
          pos.x +
            PERSON_NODE_DIMENSIONS.width / 2 -
            RELATIONSHIP_NODE_DIMENSIONS.width / 2 || 0,
        y:
          pos.y +
            PERSON_NODE_DIMENSIONS.height / 2 -
            RELATIONSHIP_NODE_DIMENSIONS.height / 2 || 0,
      },
      data: {
        id: vertexId.toString(),
        label: `Relationship ${vertexId}`,
        originalId: vertexId,
      } as PedigreeRelationshipNodeData,
    };
  };

  // ---------------------------------------------------------------------------
  // Helper function to calculate edge offset values
  const getOffsetValue = (
    graph: any,
    dg: any,
    relationships: number[],
    currentRelationshipId: number,
    currentPersonId: number
  ) => {
    if (relationships.length <= 1) {
      return undefined;
    }

    const currentRelationshipParents = graph.getParents(currentRelationshipId);
    const otherPersonId = currentRelationshipParents.find(
      (p: number) => p !== currentPersonId
    );

    if (!otherPersonId) {
      return undefined;
    }

    const currentPersonPos = dg.getPosition(currentPersonId);
    const otherPersonPos = dg.getPosition(otherPersonId);
    const isRightSideRelationship = otherPersonPos.x > currentPersonPos.x;

    // Find all relationships on the same side
    const sameSideRelationships = relationships.filter((relId) => {
      const relParents = graph.getParents(relId);
      const otherPerson = relParents.find((p: number) => p !== currentPersonId);

      if (!otherPerson) {
        return false;
      }

      const otherPos = dg.getPosition(otherPerson);
      const isDifferentGeneration = otherPos.y !== currentPersonPos.y;
      if (isDifferentGeneration) {
        return false;
      }

      const isRight = otherPos.x > currentPersonPos.x;
      return isRight === isRightSideRelationship;
    });

    // Sort same-side relationships by their partner's distance from the current person
    sameSideRelationships.sort((a, b) => {
      const aParents = graph.getParents(a);
      const bParents = graph.getParents(b);
      const aOtherPerson = aParents.find((p: number) => p !== currentPersonId);
      const bOtherPerson = bParents.find((p: number) => p !== currentPersonId);
      if (!aOtherPerson || !bOtherPerson) {
        return 0;
      }

      const aPos = dg.getPosition(aOtherPerson);
      const bPos = dg.getPosition(bOtherPerson);
      const aDistance = Math.abs(aPos.x - currentPersonPos.x);
      const bDistance = Math.abs(bPos.x - currentPersonPos.x);

      return aDistance - bDistance;
    });

    const sideIndex = sameSideRelationships.indexOf(currentRelationshipId);

    // The innermost relationship on each side (index 0) gets no offset
    if (sideIndex === 0) {
      return undefined;
    }

    return {
      index: sideIndex,
      total: sameSideRelationships.length - 1,
    };
  };

  // ---------------------------------------------------------------------------
  // Helper function to create parent-to-relationship edges
  const createParentToRelationshipEdges = (
    graph: any,
    dg: any,
    relationshipId: number,
    parents: number[],
    partnerRelationships: Map<number, Set<number>>,
    edgeIds: Set<string>,
    flowEdges: Edge[]
  ) => {
    const [parent1, parent2] = parents;

    // Get sorted relationships for each parent
    const parent1Relationships = Array.from(
      partnerRelationships.get(parent1) || []
    ).sort((a, b) => {
      const aPos = dg.getPosition(
        graph.getParents(a).find((p: number) => p !== parent1) || a
      );

      const bPos = dg.getPosition(
        graph.getParents(b).find((p: number) => p !== parent1) || b
      );

      return aPos.x - bPos.x;
    });

    const parent2Relationships = Array.from(
      partnerRelationships.get(parent2) || []
    ).sort((a, b) => {
      const aPos = dg.getPosition(
        graph.getParents(a).find((p: number) => p !== parent2) || a
      );

      const bPos = dg.getPosition(
        graph.getParents(b).find((p: number) => p !== parent2) || b
      );

      return aPos.x - bPos.x;
    });

    const parentAPos = dg.getPosition(parent1);
    const parentBPos = dg.getPosition(parent2);
    const parentALeft = parentAPos.x < parentBPos.x;
    const nodeData = getNodeData(relationshipId);

    // Create edge from first parent
    const edge1Id = `${parent1}-${relationshipId}`;
    if (!edgeIds.has(edge1Id)) {
      edgeIds.add(edge1Id);
      const offsetValue = getOffsetValue(
        graph,
        dg,
        parent1Relationships,
        relationshipId,
        parent1
      );

      flowEdges.push({
        type: 'spouse-to-relationship',
        id: edge1Id,
        source: parent1.toString(),
        target: relationshipId.toString(),
        sourceHandle: parentALeft ? 'right-source' : 'left-source',
        targetHandle: parentALeft ? 'left' : 'right',
        style: EDGE_STYLE,
        data: {
          isConsanguineous: nodeData?.isConsanguineous || false,
          relationshipType: nodeData?.relationshipType,
          offsetValue,
        },
      });
    }

    // Create edge from second parent
    const edge2Id = `${parent2}-${relationshipId}`;
    if (!edgeIds.has(edge2Id)) {
      edgeIds.add(edge2Id);
      const offsetValue = getOffsetValue(
        graph,
        dg,
        parent2Relationships,
        relationshipId,
        parent2
      );

      flowEdges.push({
        id: edge2Id,
        type: 'spouse-to-relationship',
        source: parent2.toString(),
        target: relationshipId.toString(),
        sourceHandle: parentALeft ? 'left-source' : 'right-source',
        targetHandle: parentALeft ? 'right' : 'left',
        style: EDGE_STYLE,
        data: {
          isConsanguineous: nodeData?.isConsanguineous || false,
          relationshipType: nodeData?.relationshipType,
          offsetValue,
        },
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Helper function to create relationship-to-child edges
  const createRelationshipToChildEdges = (
    graph: any,
    dg: any,
    relationshipId: number,
    edgeIds: Set<string>,
    flowEdges: Edge[]
  ) => {
    const childhubId = graph.getRelationshipChildhub(relationshipId);
    if (isNil(childhubId)) {
      return;
    }

    const children = graph.getOutEdges(childhubId);
    children.forEach((childId: number) => {
      if (!graph.isPerson(childId)) {
        return;
      }

      const edgeId = `${relationshipId}-${childId}`;
      if (edgeIds.has(edgeId)) {
        return;
      }

      edgeIds.add(edgeId);
      const twins = graph.getAllTwinsOf(childId);
      if (twins.length > 1) {
        // Handle twins with special positioning
        const twinPositions = twins.map((twinId: number) =>
          dg.getPosition(twinId)
        );
        const twinGroupCenterX =
          twinPositions.reduce(
            (sum: number, pos: { x: number; y: number }) => sum + pos.x,
            0
          ) / twins.length;

        flowEdges.push({
          id: edgeId,
          type: 'relationship-to-child',
          source: relationshipId.toString(),
          target: childId.toString(),
          sourceHandle: 'bottom',
          targetHandle: 'top',
          style: EDGE_STYLE,
          data: {
            stepHeight: DEFAULT_STEP_HEIGHT,
            twinGroupCenterX:
              twinGroupCenterX + PERSON_NODE_DIMENSIONS.width / 2,
          },
        });
      } else {
        // Regular edge for non-twins
        flowEdges.push({
          id: edgeId,
          type: 'relationship-to-child',
          source: relationshipId.toString(),
          target: childId.toString(),
          sourceHandle: 'bottom',
          targetHandle: 'top',
          style: EDGE_STYLE,
          data: {
            stepHeight: DEFAULT_STEP_HEIGHT,
          },
        });
      }
    });
  };

  // ---------------------------------------------------------------------------
  // Main function to convert pedigree to flow format
  const convertPedigreeToFlow = (graph: any) => {
    const flowNodes: PedigreeNode[] = [];
    const flowEdges: Edge[] = [];
    const edgeIds = new Set<string>(); // Initialize graphs and count partners
    const { pg, dg } = initializePedigreeGraphs(graph);
    const partnerRelationships = countPartners(graph);

    // Convert vertices to nodes
    for (let i = 0; i <= graph.getMaxRealVertexId(); i++) {
      if (graph.isPerson(i)) {
        const personNode = createPersonNode(graph, dg, i);
        flowNodes.push(personNode);
      } else if (graph.isRelationship(i)) {
        const relationshipNode = createRelationshipNode(dg, i);
        flowNodes.push(relationshipNode);
        const parents = graph.getParents(i);
        if (parents.length === 2) {
          createParentToRelationshipEdges(
            graph,
            dg,
            i,
            parents,
            partnerRelationships,
            edgeIds,
            flowEdges
          );
        }
      }
    }

    // Create edges from relationships to children
    for (let i = 0; i <= graph.getMaxRealVertexId(); i++) {
      if (graph.isRelationship(i)) {
        createRelationshipToChildEdges(graph, dg, i, edgeIds, flowEdges);
      }
    }

    // Set the graphs in context
    setPositionedGraph(pg);
    setDynamicGraph(dg);

    // Detect overlapping edges and update their styles
    const edgeMetadata = detectOverlappingEdges(flowEdges, flowNodes);
    flowEdges.forEach((edge) => {
      const metadata = edgeMetadata.get(edge.id);
      if (metadata && edge.data) {
        edge.data.stepHeight = metadata.stepHeight;
      }
    });

    return { nodes: flowNodes, edges: flowEdges };
  };

  return { nodes, edges, onNodesChange, onEdgesChange };
};
